import {
  contextRulesListResponseSchema,
  contextRuleViewSchema,
  contextSourceTextResponseSchema,
  contextVersionDiffResponseSchema,
  contextSourceActivationResponseSchema,
  contextSourceSchema,
  contextSourcesListResponseSchema,
  contextSourceUploadUrlResponseSchema,
  contextVersionsListResponseSchema,
  type ContextRulePatchBody,
  type ContextRulesListResponse,
  type ContextRuleView,
  type ContextSourceTextResponse,
  type ContextVersionDiffResponse,
  type ContextSource,
  type ContextSourceActivationResponse,
  type ContextSourceCreateBody,
  type ContextSourcesListResponse,
  type ContextSourceUploadUrlBody,
  type ContextSourceUploadUrlResponse,
  type ContextVersionsListResponse,
} from "@bitacorapro/api-types";
import { request } from "../client";

// The per-client AI context layer (ADR-0173 fase 2): the documents a
// brand uploads, their extraction state, the rules review counts and the
// versions the brand runs. Every response is parsed against the wire
// schema so a field the server adds never reaches a screen unvalidated.
export const contextApi = {
  // GET /context-sources?brandId= — one brand's documents, newest first.
  listSources: async (brandId: string): Promise<ContextSourcesListResponse> => {
    const json = await request<unknown>(
      "GET",
      "core",
      `/context-sources?brandId=${encodeURIComponent(brandId)}`
    );
    return contextSourcesListResponseSchema.parse(json);
  },
  getSource: async (id: string): Promise<ContextSource> => {
    const json = await request<unknown>("GET", "core", `/context-sources/${id}`);
    return contextSourceSchema.parse(json);
  },
  // POST /context-sources/upload-url — presigned PUT into the private
  // bucket (no public URL ever comes back).
  requestUploadUrl: async (
    body: ContextSourceUploadUrlBody
  ): Promise<ContextSourceUploadUrlResponse> => {
    const json = await request<unknown>("POST", "core", "/context-sources/upload-url", { body });
    return contextSourceUploadUrlResponseSchema.parse(json);
  },
  // POST /context-sources — after the PUT: HEAD-verified, deduplicated by
  // sha256 per brand, enqueued for extraction.
  registerSource: async (body: ContextSourceCreateBody): Promise<ContextSource> => {
    const json = await request<unknown>("POST", "core", "/context-sources", { body });
    return contextSourceSchema.parse(json);
  },
  retryExtraction: async (id: string): Promise<ContextSource> => {
    const json = await request<unknown>("POST", "core", `/context-sources/${id}/retry-extraction`);
    return contextSourceSchema.parse(json);
  },
  activateSource: async (
    id: string,
    body?: { label?: string }
  ): Promise<ContextSourceActivationResponse> => {
    const json = await request<unknown>("POST", "core", `/context-sources/${id}/activate`, {
      body: body ?? {},
    });
    return contextSourceActivationResponseSchema.parse(json);
  },
  archiveSource: async (id: string): Promise<ContextSourceActivationResponse> => {
    const json = await request<unknown>("POST", "core", `/context-sources/${id}/archive`);
    return contextSourceActivationResponseSchema.parse(json);
  },
  // GET /context-sources/:id/rules — the review list with the counts the
  // detail screen gates «Activar» on (PR-7 renders the rules themselves).
  listRules: async (sourceId: string): Promise<ContextRulesListResponse> => {
    const json = await request<unknown>("GET", "core", `/context-sources/${sourceId}/rules`);
    return contextRulesListResponseSchema.parse(json);
  },
  getSourceText: async (sourceId: string): Promise<ContextSourceTextResponse> => {
    const json = await request<unknown>("GET", "core", `/context-sources/${sourceId}/text`);
    return contextSourceTextResponseSchema.parse(json);
  },
  // PATCH /context-rules/:id — every save is version v+1 on the server.
  patchRule: async (id: string, body: ContextRulePatchBody): Promise<ContextRuleView> => {
    const json = await request<unknown>("PATCH", "core", `/context-rules/${id}`, { body });
    return contextRuleViewSchema.parse(json);
  },
  acceptRule: async (id: string): Promise<ContextRuleView> => {
    const json = await request<unknown>("POST", "core", `/context-rules/${id}/accept`);
    return contextRuleViewSchema.parse(json);
  },
  discardRule: async (id: string): Promise<ContextRuleView> => {
    const json = await request<unknown>("POST", "core", `/context-rules/${id}/discard`);
    return contextRuleViewSchema.parse(json);
  },
  resolveConflict: async (id: string, discardRuleIds: string[]): Promise<ContextRuleView> => {
    const json = await request<unknown>("POST", "core", `/context-rules/${id}/resolve-conflict`, {
      body: { discardRuleIds },
    });
    return contextRuleViewSchema.parse(json);
  },
  diffVersions: async (id: string, against: string): Promise<ContextVersionDiffResponse> => {
    const json = await request<unknown>(
      "GET",
      "core",
      `/context-versions/${id}/diff?against=${encodeURIComponent(against)}`
    );
    return contextVersionDiffResponseSchema.parse(json);
  },
  listVersions: async (brandId: string): Promise<ContextVersionsListResponse> => {
    const json = await request<unknown>(
      "GET",
      "core",
      `/context-versions?brandId=${encodeURIComponent(brandId)}`
    );
    return contextVersionsListResponseSchema.parse(json);
  },
};
